var NpawObject = require('../object')
var { default: Log } = require('../../common/log')
var { AnalyticsTag } = require('../../common/Constants')
/**
 * This static class provides device detection methods.
 *
 * @class
 * @static
 * @memberof npaw
 */
var BackgroundDetector = NpawObject.extend({
  constructor: function (plugin, session) {
    this.plugin = plugin;
    this.session = session;
    this.isInBackground = false;
    this.listenerReference = this._visibilityListener.bind(this);
    this.appStateListenerReference = this._appStateListener.bind(this);
    this._reset();
  },

  startDetection: function () {
    if (this.isBackgroundDetectorStarted) {
      return;
    }
    if (typeof document !== 'undefined' && document.addEventListener) {
      this.isBackgroundDetectorStarted = true;
      document.addEventListener('visibilitychange', this.listenerReference);
      return;
    }
    // No DOM (e.g. React Native runtime): fall back to react-native AppState.
    var appState = this._getAppState();
    if (appState && typeof appState.addEventListener === 'function') {
      this.isBackgroundDetectorStarted = true;
      this.appState = appState;
      this.appStateSubscription = appState.addEventListener('change', this.appStateListenerReference);
    }
  },

  stopDetection: function () {
    if (!this.isBackgroundDetectorStarted) {
      return;
    }
    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener('visibilitychange', this.listenerReference);
    }
    // react-native >= 0.65 returns a subscription with remove(); older versions expose removeEventListener.
    if (this.appStateSubscription && typeof this.appStateSubscription.remove === 'function') {
      this.appStateSubscription.remove();
    } else if (this.appState && typeof this.appState.removeEventListener === 'function') {
      this.appState.removeEventListener('change', this.appStateListenerReference);
    }
    this._reset();
  },

  _reset: function () {
    this.isBackgroundDetectorStarted = false;
    this.appState = null;
    this.appStateSubscription = null;
  },

  _visibilityListener: function (e) {
    if (typeof document !== 'undefined') {
      var settings = this._getSettings();
      if (document.visibilityState === 'hidden') {
        this._toBackground(settings);
      } else if (document.visibilityState === 'visible') {
        this._toForeground(settings);
      }
    }
  },

  _appStateListener: function (nextAppState) {
    var settings = this._getSettings();
    // Only 'background' means backgrounded; 'inactive' (iOS: control/notification center,
    // incoming calls, permission dialogs) is transitional and must not close the view.
    // This matches the web semantics, where visibilitychange 'hidden' == 'background'.
    if (nextAppState === 'background' && !this.isInBackground) {
      this._toBackground(settings);
    } else if (nextAppState === 'active' && this.isInBackground) {
      this._toForeground(settings);
    }
  },

  _toBackground: function (settings) {
    this.isInBackground = true;
    try {
      if (this.plugin && this.plugin.videos) {
        for (var i = 0; i < this.plugin.videos.getVideoKeys().length; i++) {
          var videoKey = this.plugin.videos.getVideoKeys()[i];
          var adsAdapter = this.plugin.videos.getAdsAdapter(videoKey);
          if (adsAdapter) {
            adsAdapter.stopChronoView();
          }
        }
      }
    } catch (e) {
      Log.debug(AnalyticsTag, 'Error stopping ads adapter chronos in background:', e);
    }
    try {
      if (typeof settings === 'string') {
        switch (settings) {
          case 'stop':
            this._fireStop();
            break;
          case 'pause':
            this._firePause();
            break;
        }

        this.session.suspend();
      }
    } catch (e) {
      Log.debug(AnalyticsTag, 'Error suspending session in background:', e);
    }
  },

  _toForeground: function (settings) {
    this.isInBackground = false;
    // ads
    try {
      if (this.plugin && this.plugin.videos) {
        for (var i = 0; i < this.plugin.videos.getVideoKeys().length; i++) {
          var videoKey = this.plugin.videos.getVideoKeys()[i];
          var adsAdapter = this.plugin.videos.getAdsAdapter(videoKey);
          if (adsAdapter) {
            adsAdapter.startChronoView();
          }
        }
      }
    } catch (e) {
      Log.debug(AnalyticsTag, 'Error starting ads adapter chronos in foreground:', e);
    }
    // nothing for video yet
    try {
      if (typeof settings === 'string' && settings) {
        this.session.resume();
      }
    } catch (e) {
      Log.debug(AnalyticsTag, 'Error resuming session in foreground:', e);
    }
  },

  _getSettings: function () {
    if (typeof this.plugin.options['background.settings'] === 'string' && this.plugin.options['background.settings']) {
      return this.plugin.options['background.settings'];
    }
    if (this.plugin.deviceDetector.isSmartTV()) {
      return this.plugin.options['background.settings.tv'];
    }
    if (this.plugin.deviceDetector.isDesktop()) {
      return this.plugin.options['background.settings.desktop'];
    }
    if (this.plugin.deviceDetector.isAndroid()) {
      return this.plugin.options['background.settings.android'];
    }
    if (this.plugin.deviceDetector.isIphone()) {
      return this.plugin.options['background.settings.iOS'];
    }
    if (this.plugin.deviceDetector.isPlayStation()) {
      return this.plugin.options['background.settings.playstation'];
    }
  },

  _getAppState: function () {
    // AppState is injected on the global by the React Native entry (NpawPlugin.ts), which
    // can require 'react-native' (a webpack external Metro resolves at runtime). This shared
    // file cannot import it directly because it is also compiled into the browser build, and
    // the RN runtime exposes no global require. In the browser this global is never set, so
    // the DOM path above is used instead.
    try {
      var g =
        (typeof globalThis !== 'undefined' && globalThis) ||
        (typeof global !== 'undefined' && global) ||
        null;
      var appState = g && g.__NPAW_APP_STATE__;
      if (appState && typeof appState.addEventListener === 'function') {
        return appState;
      }
    } catch (e) {
      Log.debug(AnalyticsTag, 'AppState not available for background detection:', e);
    }
    return null;
  },

  _firePause: function () {
    this._fireX('firePause');
  },

  _fireStop: function () {
    this._fireX('fireStop');
  },

  _fireX: function (fireMethod) {
    try {
      if (this.plugin && this.plugin.videos) {
        for (var i = 0; i < this.plugin.videos.getVideoKeys().length; i++) {
          var videoKey = this.plugin.videos.getVideoKeys()[i];
          this.adsAdapter = this.plugin.videos.getAdsAdapter(videoKey);
          if (this.adsAdapter) {
            this.adsAdapter[fireMethod]({}, 'backgroundDetector');
          }
          this.contentAdapter = this.plugin.videos.getAdapter(videoKey);
          if (this.contentAdapter) {
            this.contentAdapter[fireMethod]({}, 'backgroundDetector');
          }
        }
      }
    } catch (e) {
      Log.debug(AnalyticsTag, 'Error firing ' + fireMethod + ' from background detector:', e);
    }
  },

  canBlockStartCalls: function () {
    return (
      this.isInBackground && this.plugin.options['background.enabled'] === true && this._getSettings() // it is stop or pause
    );
  }
});

module.exports = BackgroundDetector;
